import { Camera, FileText, TriangleAlert, Upload, X } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate } from '../format-date';
import type { ReportDocument } from './api-parity';
import {
  MAX_LABEL,
  MAX_NOTE_FILES,
  sizeText,
  type PendingFile,
  type RefusedFile,
} from './note-files';

/** «Documenti della nota» (U1 board 1.1, 1.3, 1.4): the box around the rows and the pickers. */
export function FilesBox({
  count,
  hint,
  children,
}: {
  readonly count: number;
  readonly hint: string;
  readonly children: React.ReactNode;
}) {
  const { t } = useTranslation('expenses-parity');
  return (
    // #952 L3: the standard card border; a bare `border` took the text colour and looked selected.
    <section
      className="border-border bg-card flex flex-col gap-2 rounded-md border p-3"
      aria-label={t('files.title')}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('files.title')}</h3>
        <span className="text-muted-foreground text-xs">
          {t('files.count', { count, max: MAX_NOTE_FILES })}
        </span>
      </div>
      <p className="text-muted-foreground text-sm">{hint}</p>
      {children}
    </section>
  );
}

/** One file: its name or label, a line under it, and ✕ when it may go. */
export function FileRow({
  title,
  detail,
  onRemove,
  onOpen,
  children,
}: {
  readonly title: string;
  readonly detail: string;
  readonly onRemove?: (() => void) | undefined;
  readonly onOpen?: (() => void) | undefined;
  readonly children?: React.ReactNode;
}) {
  const { t } = useTranslation('expenses-parity');
  return (
    <div className="flex items-start gap-2 rounded-md border p-2">
      <FileText className="text-muted-foreground mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {onOpen ? (
          <button
            type="button"
            className="text-left text-sm font-semibold hover:underline"
            onClick={onOpen}
          >
            {title}
          </button>
        ) : (
          <b className="text-sm">{title}</b>
        )}
        <small className="text-muted-foreground break-words">{detail}</small>
        {children}
      </div>
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('files.remove', { name: title })}
          onClick={onRemove}
        >
          <X />
        </Button>
      ) : null}
    </div>
  );
}

/** The line under a saved file: pages, size, who added it and when. */
export function useSavedDetail(): (file: ReportDocument) => string {
  const { t } = useTranslation('expenses-parity');
  return (file) => {
    const size = sizeText(file.sizeBytes);
    return t('files.saved', {
      count: file.pages,
      amount: size.amount,
      unit: size.unit,
      by: file.createdBy,
      on: formatDate(file.createdAt),
    });
  };
}

/** A refused file says why and what to do; the others go ahead (board 1.3). */
export function RefusedRows({
  refused,
  onDismiss,
}: {
  readonly refused: readonly RefusedFile[];
  readonly onDismiss: (key: string) => void;
}) {
  const { t } = useTranslation('expenses-parity');
  return refused.map((file) => (
    <div
      key={file.key}
      role="alert"
      className="border-destructive flex items-start gap-2 rounded-md border p-2"
    >
      <TriangleAlert className="text-destructive mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col">
        <b className="text-sm break-words">{file.name}</b>
        <small className="text-destructive">
          {t(`files.refused.${file.reason}`, { size: file.sizeMb, max: MAX_NOTE_FILES })}
        </small>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('files.dismiss')}
        onClick={() => onDismiss(file.key)}
      >
        <X />
      </Button>
    </div>
  ));
}

/**
 * «Scatta foto» and «Carica PDF» on the phone; a drop area with «scegli dal computer» from md up.
 * At 16 files the pickers give way to the explanation (board 1.3).
 */
export function FilePickers({
  full,
  busy,
  onFiles,
}: {
  readonly full: boolean;
  readonly busy: boolean;
  readonly onFiles: (files: File[]) => void;
}) {
  const { t } = useTranslation('expenses-parity');
  const photos = useRef<HTMLInputElement>(null);
  const pdfs = useRef<HTMLInputElement>(null);
  const any = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  if (full) {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t('files.full', { max: MAX_NOTE_FILES })}
      </p>
    );
  }
  const take = (list: FileList | null): void => {
    if (list && list.length > 0) onFiles([...list]);
  };
  const drop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setOver(false);
    take(event.dataTransfer.files);
  };
  return (
    <>
      {busy ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t('files.preparing')}
        </p>
      ) : null}
      <div className="flex gap-2 md:hidden">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          disabled={busy}
          onClick={() => photos.current?.click()}
        >
          <Camera />
          {t('files.photo')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          disabled={busy}
          onClick={() => pdfs.current?.click()}
        >
          <Upload />
          {t('files.pdf')}
        </Button>
      </div>
      <div
        className={`hidden flex-col items-center gap-1 rounded-md border border-dashed p-4 text-center text-sm md:flex ${over ? 'bg-muted' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
      >
        <Upload className="size-5" aria-hidden />
        <span>
          {t('files.drop')}{' '}
          <button
            type="button"
            className="font-semibold underline"
            disabled={busy}
            onClick={() => any.current?.click()}
          >
            {t('files.choose')}
          </button>
        </span>
        <small className="text-muted-foreground">
          {t('files.limits', { max: MAX_NOTE_FILES })}
        </small>
      </div>
      <input
        ref={photos}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        hidden
        aria-label={t('files.photo')}
        onChange={(event) => {
          take(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={pdfs}
        type="file"
        accept="application/pdf"
        multiple
        hidden
        aria-label={t('files.pdf')}
        onChange={(event) => {
          take(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={any}
        type="file"
        accept="application/pdf,image/*"
        multiple
        hidden
        aria-label={t('files.choose')}
        onChange={(event) => {
          take(event.target.files);
          event.target.value = '';
        }}
      />
    </>
  );
}

/** The files chosen and not sent yet, each with its optional short label (board 1.3). */
export function PendingRows({
  pending,
  onLabel,
  onDrop,
}: {
  readonly pending: readonly PendingFile[];
  readonly onLabel: (key: string, label: string) => void;
  readonly onDrop: (key: string) => void;
}) {
  const { t } = useTranslation('expenses-parity');
  return pending.map((file) => (
    <FileRow
      key={file.key}
      title={file.name}
      detail={t('files.pending', { count: file.pages, ...sizeText(file.pdf.size) })}
      onRemove={() => onDrop(file.key)}
    >
      <label className="flex flex-col gap-1 text-xs">
        {t('files.label')}
        <Input
          value={file.label}
          maxLength={MAX_LABEL}
          placeholder={t('files.labelPlaceholder')}
          onChange={(event) => onLabel(file.key, event.target.value)}
        />
      </label>
    </FileRow>
  ));
}

/** The server's refusals, by file, in words (`files.problems.*`). */
export function SendErrors({
  errors,
}: {
  readonly errors: readonly { name: string; code: string }[];
}) {
  const { t } = useTranslation('expenses-parity');
  if (errors.length === 0) return null;
  return (
    <ul role="alert" className="text-destructive flex flex-col gap-1 text-sm">
      {errors.map((error) => (
        <li key={`${error.name}-${error.code}`}>
          {t('files.notSent', {
            name: error.name,
            why: t(`files.problems.${problemOf(error.code)}`),
          })}
        </li>
      ))}
    </ul>
  );
}

const FILE_PROBLEMS = [
  'NOT_A_PDF',
  'DOCUMENT_TOO_LARGE',
  'TOO_MANY_DOCUMENTS',
  'REPORT_NOT_EDITABLE',
  'DOCUMENT_LOCKED',
] as const;
export type FileProblem = (typeof FILE_PROBLEMS)[number] | 'other';

export function problemOf(code: string): FileProblem {
  return FILE_PROBLEMS.find((known) => known === code) ?? 'other';
}
